import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import { axiosInstance, validateUuid } from "../utils";
import { CandidateFieldsConfiguration } from "./models";

export interface ClientResponse {
    id: string;
    name: string;
    description: string | undefined;
    externalId: string | undefined;
    isEnabled: boolean;
    customField01: string | undefined;
    customField02: string | undefined;
    customField03: string | undefined;
    customField04: string | undefined;
    customField05: string | undefined;
    candidateFieldsConfiguration?: CandidateFieldsConfiguration | null;
    createdOn: string | undefined;
    createdById: string | undefined;
    createdByDisplayName: string | undefined;
    updatedOn: string | undefined;
    updatedById: string | undefined;
    updatedByDisplayName: string | undefined;
    isFavorite: boolean;
    isInternal: boolean;
    roleId: string;
}

export function useClients() {
    const axios = axiosInstance();
    return useQuery({
        ...queryKeys.clients.all,
        queryFn: () => axios.get<ClientResponse[]>(`api/Clients`).then(response => response.data),
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        staleTime: 0, // Consider data always stale to force refetch
    });
}

export function useClient(clientId: string | undefined, options?: Partial<UseQueryOptions<ClientResponse>>) {
    const axios = axiosInstance();
    return useQuery({
        ...queryKeys.clients.byId(clientId ?? ""),
        enabled: !!clientId,
        queryFn: () => axios.get<ClientResponse>(`api/Clients/${validateUuid(clientId)}`).then(response => response.data),
        staleTime: 30000,
        ...options,
    });
}

export function useMutateClientFavorite() {
    const axios = axiosInstance(),
        queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: { clientId: string; action: "add" | "remove" }) =>
            request.action === "add" ? axios.put(`api/Clients/${request.clientId}/favorite`) : axios.delete(`api/Clients/${request.clientId}/favorite`),
        onSuccess: async (data, variables) => {
            await queryClient.invalidateQueries(queryKeys.clients.all.queryKey);
            await queryClient.invalidateQueries(queryKeys.clients.byId(variables.clientId).queryKey);
        },
    });
}
